import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { Loader } from 'common/react/components/Loader';
import {
    playerStatsDataTypeSelector,
    playerStatsNavSelector,
    playerStatsCustomFieldsSelector
} from 'store/modules/match-centre/player-stats/selectors';
import { EmptyState } from 'common/react/components/EmptyState';
import { Table } from './components/Table';
import {
    composeRowData,
    composeColumnData,
    filterPlayersByTeam,
    resolveRowsFromPlayers,
    resolveRowsFromAverages
} from './utils';
import { useDataType } from 'widgets/statspro/js/components/common/hooks/use-data-type';
import { UPDATE_DATA_TYPE } from 'store/modules/match-centre/player-stats/actions';
import { updateContainerDataType } from './utils/update-container-data-type';
import { useGetMatchPlayerStatsQuery } from 'store/modules/api/cfs-api';

export const PlayerStatsTable = ({ matchId, homePid, awayPid, compPid }) => {
    const matchData = useSelector((state) =>
        PULSE.app.redux.selectors.playerstats.scope(state, matchId)
    );

    const { data: matchPlayers } = useGetMatchPlayerStatsQuery({ matchId });

    const nav = useSelector(playerStatsNavSelector);
    const initialDataType = useSelector(playerStatsDataTypeSelector);
    const { dataType, updateDataType } = useDataType({
        initialDataType,
        onUpdateActionCreator: UPDATE_DATA_TYPE.request
    });
    const [rowData, setRowData] = useState([]);
    const [noDataAvailable, setNoDataAvailable] = useState(false);
    const [noPlayerData, setNoPlayerData] = useState(false);
    const INITIAL_DATA_REQUESTS = 2;

    const homeAbbr = PULSE.app.common.team.getTeamAbbr(
        matchData?.playerAverages?.find((item) => item.team.teamId === homePid)
            ?.team?.teamId ||
            matchData?.players?.find((item) => item.teamId === homePid)?.teamId
    );

    const awayAbbr = PULSE.app.common.team.getTeamAbbr(
        matchData?.playerAverages?.find((item) => item.team.teamId === awayPid)
            ?.team?.teamId ||
            matchData?.players?.find((item) => item.teamId === awayPid)?.teamId
    );

    const customFields = useSelector(playerStatsCustomFieldsSelector);
    const [requestsComplete, setRequestsComplete] = useState(
        matchData?.requestsComplete
    );

    useEffect(() => {
        // Check for requests to complete
        if (
            matchData?.requestsComplete &&
            matchData?.requestsComplete !== requestsComplete
        ) {
            setRequestsComplete(matchData?.requestsComplete);
        }
    }, [matchData?.requestsComplete]);

    useEffect(() => {
        // Update the data with nav values when requests have completed
        setNoDataAvailable(false);

        if (
            matchData?.requestsComplete &&
            matchData.requestsComplete >= INITIAL_DATA_REQUESTS &&
            homeAbbr
        ) {
            const playersData = matchData.players ?? [];

            if (playersData.length) {
                resolveRowsFromPlayers({
                    playersData: filterPlayersByTeam(playersData, nav.teamPid),
                    matchData,
                    dataType: dataType.type,
                    noPlayerData,
                    updateDataType,
                    setRowData,
                    setNoDataAvailable
                });
            } else {
                resolveRowsFromAverages({
                    matchData,
                    teamPid: nav.teamPid,
                    dataType: dataType.type,
                    noPlayerData,
                    updateDataType,
                    setNoPlayerData,
                    setRowData,
                    setNoDataAvailable
                });
            }
        } else {
            // When everything else fails, setNoDataAvailable to true
            // to prevent an infinity loader
            setNoDataAvailable(true);
        }
    }, [
        matchData,
        requestsComplete,
        dataType.type,
        nav.stats,
        nav.teamPid,
        homeAbbr
    ]);

    useEffect(() => {
        updateContainerDataType(nav.stats);
    }, [nav.stats]);

    /**
     * Get columns.
     */
    const columns = useMemo(
        () => composeColumnData(nav.stats, customFields),
        [nav.stats, rowData, customFields]
    );

    /**
     * Get rows.
     */
    const data = useMemo(
        () =>
            rowData.map((item) =>
                composeRowData(
                    item,
                    columns.filter(
                        (col) =>
                            !['jumperNumber', 'player', 'empty'].includes(
                                col.accessor
                            )
                    ),
                    dataType.type,
                    noPlayerData,
                    homeAbbr,
                    awayAbbr,
                    homePid
                )
            ),
        [columns]
    );

    /**
     * matchPlayersData variable holds player profile IDs; data variable doesn't. As we'll be
     * using the rest of the data array, we should map the profile IDs onto that array. Eventually
     * we return the object including id in the player object.
     */
    const matchPlayersData = Array.isArray(matchPlayers?.players)
        ? matchPlayers?.players
        : [];

    /**
     * idLookup example output:
     * {
            "key": "tom-atkins",
            "value": "CD_I994386"
        }
     */
    const idLookup = new Map(
        matchPlayersData.map((item) => {
            const player = item.player?.player?.player;
            const key =
                `${player?.playerName?.givenName}-${player?.playerName?.surname}`.toLowerCase();
            return [key, player?.playerId];
        })
    );

    /**
     * updatedMatchPlayersData entry example:
     * {
            "id": "CD_I994386",
            "givenName": "Tom",
            "surname": "Atkins"
        }
     */
    const updatedMatchPlayersData = data.map((item) => {
        const key =
            `${item.player?.givenName}-${item.player?.surname}`.toLowerCase();
        const playerId = idLookup.get(key) || null;

        return {
            ...item,
            player: {
                playerId: playerId,
                givenName: item.player.givenName,
                surname: item.player.surname
            }
        };
    });

    if (!data.length && !noDataAvailable) {
        // Loader for waiting for data
        return (
            <div className="mc-player-stats-table__loader">
                <Loader />
            </div>
        );
    } else if (noDataAvailable) {
        // Error handling for when there is no data
        return (
            <div className="mc-player-stats-table__empty-state">
                <EmptyState
                    titleTranslation="label.stats.error.noStatsAvailable"
                    summaryTranslation="label.stats.error.noStatsAvailable.summary"
                />
            </div>
        );
    }

    return (
        <Table
            key={nav.stats + customFields.length}
            columns={columns}
            data={updatedMatchPlayersData}
            modifier="player-stats"
            compPid={compPid}
            matchId={matchId}
        />
    );
};

PlayerStatsTable.propTypes = {
    matchId: PropTypes.string.isRequired,
    homePid: PropTypes.string.isRequired,
    awayPid: PropTypes.string.isRequired,
    compPid: PropTypes.string,
    homeNickname: PropTypes.string.isRequired,
    awayNickname: PropTypes.string.isRequired
};
